/**
 * Tipos da funcionalidade de "marcar conteúdo como visto" (userContentViewHistory).
 * Ver doc "Visualização de Conteúdo (Marcar como visto) — Guia Frontend".
 */

/** Tipo de conteúdo aceito pela API de visualização. */
export type ViewContentType = 'lesson' | 'material' | 'exercise';

/** De onde o conteúdo foi visto. Se omitido, o backend usa `free_browsing`. */
export type ViewOrigin = 'courses' | 'custom_schedule' | 'free_browsing';

/** Subtipos de material que possuem endpoint `/:slug/opened` próprio. */
export type MaterialViewType = 'workbook' | 'mindmap' | 'podcast';

/** Status atual de visualização de um conteúdo (fonte da verdade da UI). */
export interface IViewStatus {
  contentType: ViewContentType;
  content: string;
  viewed: boolean;
  origin: ViewOrigin | null;
  updatedAt: string;
}

/** Resposta padrão dos endpoints que retornam status de visualização. */
export interface IViewStatusResponse {
  ok: boolean;
  /** Presente no toggle de aula/material e no finish de exercício. */
  viewStatus?: IViewStatus;
  /** Comportamento antigo de conclusão de aula (mantido). */
  result?: unknown;
  /** Presente no endpoint genérico POST /content-views. */
  status?: IViewStatus;
}

export interface IContentViewHistoryEntry {
  viewed: boolean;
  origin: ViewOrigin;
  changedAt: string;
}

export interface IContentViewHistoryItem {
  user: string;
  contentType: ViewContentType;
  content: string;
  viewed: boolean;
  origin: ViewOrigin | null;
  history: IContentViewHistoryEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface IContentViewHistoryResponse {
  items: IContentViewHistoryItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/**
 * Extrai o `viewStatus.viewed` de uma resposta de toggle/finish para atualizar a
 * UI. Faz fallback para `true` quando a resposta não traz `viewStatus` (ex.:
 * endpoint mobile de curso que responde apenas `{ ok: true }`).
 */
export function getViewedFromResponse(response?: IViewStatusResponse): boolean {
  return response?.viewStatus?.viewed ?? response?.status?.viewed ?? true;
}

/** Mapeia o `file_category`/`fileCategory` do material para o subtipo de view. */
export function materialViewTypeFromCategory(
  category?: string
): MaterialViewType {
  switch (category) {
    case 'mapa_mental':
    case 'mindmap':
      return 'mindmap';
    case 'podcast':
    case 'audio':
      return 'podcast';
    default:
      return 'workbook';
  }
}
